import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Beer, TrendingUp, Bone, BarChart3, MessageCircle, Mail, Zap, Users } from "lucide-react";
import { KILTRO_BRAND } from "../lib/assets";

export default function ForBusinesses() {
  const { t } = useTranslation();
  const faqs = [1, 2, 3, 4, 5].map((n) => ({
    q: t(`forBusiness.faq${n}Q`),
    a: t(`forBusiness.faq${n}A`),
  }));
  return (
    <div className="bg-black text-[#F5F5F5]" data-testid="for-business-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b-2 border-[#1A1A1A]">
        <div className="kiltro-stripes h-3" />
        <div className="max-w-6xl mx-auto px-4 lg:px-8 py-20">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FEBA01] flex items-center gap-2">
            <Beer size={12} /> {t("forBusiness.eyebrow")}
          </div>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight leading-[1]">
            {t("forBusiness.title1")} <br />
            <span className="inline-block bg-[#FEBA01] text-black px-3 py-1 -skew-x-[6deg]">{t("forBusiness.title2")}</span>
          </h1>
          <p className="mt-6 max-w-2xl text-base lg:text-lg text-[#E5E5E5] leading-relaxed">
            {t("forBusiness.lead")}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href={`https://wa.me/${KILTRO_BRAND.whatsapp.replace(/\D/g, "")}?text=${encodeURIComponent(t("forBusiness.ctaWhatsappMsg"))}`}
              target="_blank"
              rel="noreferrer"
              className="btn-brutal inline-flex items-center gap-2 bg-[#FEBA01] text-black font-display text-lg uppercase tracking-wide px-6 py-3 shadow-brutal-white"
              data-testid="b2b-whatsapp-cta"
            >
              <MessageCircle size={18} /> {t("forBusiness.ctaWhatsapp")}
            </a>
            <a
              href={`mailto:${KILTRO_BRAND.contactEmail}?subject=${encodeURIComponent(t("forBusiness.ctaEmailSubject"))}`}
              className="inline-flex items-center gap-2 border-2 border-[#FEBA01] text-[#FEBA01] font-display text-lg uppercase tracking-wide px-6 py-3 hover:bg-[#FEBA01] hover:text-black transition-colors"
              data-testid="b2b-email-cta"
            >
              <Mail size={18} /> {t("forBusiness.ctaEmail")}
            </a>
          </div>

          <div className="mt-10 grid sm:grid-cols-3 gap-3 max-w-3xl">
            <KPI value="$0" label={t("forBusiness.kpi1")} />
            <KPI value="48h" label={t("forBusiness.kpi2")} />
            <KPI value="100%" label={t("forBusiness.kpi3")} />
          </div>
        </div>
      </section>

      {/* ¿Cómo funciona? */}
      <section className="max-w-6xl mx-auto px-4 lg:px-8 py-16">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#FEBA01]">{t("forBusiness.methodEyebrow")}</div>
          <h2 className="mt-2 font-display text-3xl sm:text-4xl uppercase tracking-tight">{t("forBusiness.methodTitle")}</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          <Step n={1} title={t("forBusiness.step1Title")} desc={t("forBusiness.step1Desc")} icon={Bone} />
          <Step n={2} title={t("forBusiness.step2Title")} desc={t("forBusiness.step2Desc")} icon={Zap} />
          <Step n={3} title={t("forBusiness.step3Title")} desc={t("forBusiness.step3Desc")} icon={Users} />
        </div>
      </section>

      {/* Beneficios */}
      <section className="bg-[#0F0F0F] border-y-2 border-[#1A1A1A]">
        <div className="max-w-6xl mx-auto px-4 lg:px-8 py-16 grid md:grid-cols-3 gap-4">
          <Benefit icon={TrendingUp} title={t("forBusiness.ben1Title")} desc={t("forBusiness.ben1Desc")} />
          <Benefit icon={BarChart3} title={t("forBusiness.ben2Title")} desc={t("forBusiness.ben2Desc")} />
          <Benefit icon={Users} title={t("forBusiness.ben3Title")} desc={t("forBusiness.ben3Desc")} />
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 lg:px-8 py-16">
        <div className="text-center mb-8">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#FEBA01]">{t("forBusiness.faqEyebrow")}</div>
          <h2 className="mt-2 font-display text-3xl sm:text-4xl uppercase tracking-tight">{t("forBusiness.faqTitle")}</h2>
        </div>
        <div className="space-y-3">
          {faqs.map((f, i) => <Faq key={i} q={f.q} a={f.a} idx={i} />)}
        </div>
      </section>

      {/* CTA final */}
      <section className="max-w-6xl mx-auto px-4 lg:px-8 py-16">
        <div className="bg-[#FEBA01] text-black p-8 lg:p-12 text-center relative overflow-hidden">
          <div className="kiltro-stripes absolute inset-0 opacity-10" />
          <div className="relative">
            <h2 className="font-display text-3xl sm:text-5xl uppercase tracking-tight leading-[1]">
              {t("forBusiness.finalTitle1")} <br /> {t("forBusiness.finalTitle2")}
            </h2>
            <p className="mt-3 text-base font-medium max-w-xl mx-auto">{t("forBusiness.finalDesc")}</p>
            <div className="mt-6 flex flex-wrap gap-3 justify-center">
              <a
                href={`https://wa.me/${KILTRO_BRAND.whatsapp.replace(/\D/g, "")}?text=${encodeURIComponent(t("forBusiness.finalCtaMsg"))}`}
                target="_blank"
                rel="noreferrer"
                className="btn-brutal bg-black text-[#FEBA01] font-display text-lg uppercase px-6 py-3 inline-flex items-center gap-2"
                data-testid="b2b-final-cta"
              >
                <MessageCircle size={18} /> {t("forBusiness.finalCta")}
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function KPI({ value, label }) {
  return (
    <div className="border-l-2 border-[#FEBA01] pl-3">
      <div className="font-display text-3xl text-[#FEBA01]">{value}</div>
      <div className="font-mono text-[10px] uppercase tracking-widest text-[#A3A3A3] leading-tight mt-1">{label}</div>
    </div>
  );
}

function Step({ n, title, desc, icon: Icon }) {
  return (
    <div className="bg-[#1A1A1A] border-2 border-[#333] p-6 hover:border-[#FEBA01] transition relative">
      <div className="absolute -top-4 -left-4 w-10 h-10 bg-[#FEBA01] text-black flex items-center justify-center font-display text-2xl">
        {n}
      </div>
      <Icon size={28} className="text-[#FEBA01] mb-3 mt-2" />
      <h3 className="font-display text-xl uppercase tracking-tight">{title}</h3>
      <p className="mt-2 text-sm text-[#A3A3A3] leading-relaxed">{desc}</p>
    </div>
  );
}

function Benefit({ icon: Icon, title, desc }) {
  return (
    <div className="p-5">
      <div className="w-12 h-12 bg-[#FEBA01] text-black flex items-center justify-center"><Icon size={24} /></div>
      <h3 className="mt-4 font-display text-2xl uppercase tracking-tight">{title}</h3>
      <p className="mt-2 text-sm text-[#A3A3A3] leading-relaxed">{desc}</p>
    </div>
  );
}

function Faq({ q, a, idx }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-[#1A1A1A] border-2 border-[#333]">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left"
        data-testid={`b2b-faq-${idx}`}
      >
        <span className="font-display uppercase text-base">{q}</span>
        <span className="text-[#FEBA01] font-display text-2xl shrink-0">{open ? "−" : "+"}</span>
      </button>
      {open && <div className="px-4 pb-4 text-sm text-[#A3A3A3] leading-relaxed">{a}</div>}
    </div>
  );
}
